import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { STAFF_TRACKING_PENDING_STATUS_ID } from "accountOwner/pages/OfficePolicyManagement/tabs/StaffTracking/StaffTrackingPolicyStatus";

const StaffTrackingSelectionContext = createContext(null);

/**
 * Selection state for Staff Tracking bulk actions (header menu + row checkboxes).
 */
export function StaffTrackingSelectionProvider({ children }) {
  const [trackingRows, setTrackingRows] = useState([]);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);
  const [isArchivedStaffList, setIsArchivedStaffList] = useState(false);
  const refetchRef = useRef(null);

  const clearStaffSelection = useCallback(() => {
    setSelectedAssignmentIds([]);
  }, []);

  const setStaffTrackingRefetch = useCallback((fn) => {
    refetchRef.current = typeof fn === "function" ? fn : null;
  }, []);

  const refetchStaffTracking = useCallback(() => {
    refetchRef.current?.();
  }, []);

  const toggleAssignment = useCallback((policyAssignmentId, checked) => {
    const id = Number(policyAssignmentId);
    if (!id) return;
    setSelectedAssignmentIds((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  }, []);

  const selectAllAssignments = useCallback((rows) => {
    const ids = (rows || [])
      .map((r) => Number(r.policyAssignmentId ?? r.id))
      .filter(Boolean);
    setSelectedAssignmentIds(ids);
  }, []);

  const hasStaffSelection = selectedAssignmentIds.length > 0;

  /** Selected rows whose status is Pending (for bulk Re-notify / Cancel). */
  const pendingSelectedAssignmentIds = useMemo(() => {
    const pending = STAFF_TRACKING_PENDING_STATUS_ID;
    return selectedAssignmentIds.filter((aid) => {
      const row = trackingRows.find(
        (r) => Number(r.policyAssignmentId ?? r.id) === aid,
      );
      return row != null && Number(row.status) === pending;
    });
  }, [trackingRows, selectedAssignmentIds]);

  /** True if at least one selected row is Pending — enables global Re-notify / Cancel. */
  const hasAnySelectedPending = pendingSelectedAssignmentIds.length > 0;

  const value = useMemo(
    () => ({
      trackingRows,
      setTrackingRows,
      selectedAssignmentIds,
      setSelectedAssignmentIds,
      isArchivedStaffList,
      setIsArchivedStaffList,
      clearStaffSelection,
      toggleAssignment,
      selectAllAssignments,
      hasStaffSelection,
      pendingSelectedAssignmentIds,
      hasAnySelectedPending,
      setStaffTrackingRefetch,
      refetchStaffTracking,
    }),
    [
      trackingRows,
      selectedAssignmentIds,
      isArchivedStaffList,
      clearStaffSelection,
      toggleAssignment,
      selectAllAssignments,
      hasStaffSelection,
      pendingSelectedAssignmentIds,
      hasAnySelectedPending,
      setStaffTrackingRefetch,
      refetchStaffTracking,
    ],
  );

  return (
    <StaffTrackingSelectionContext.Provider value={value}>
      {children}
    </StaffTrackingSelectionContext.Provider>
  );
}

export function useStaffTrackingSelection() {
  const ctx = useContext(StaffTrackingSelectionContext);
  if (!ctx) {
    return {
      trackingRows: [],
      setTrackingRows: () => {},
      selectedAssignmentIds: [],
      setSelectedAssignmentIds: () => {},
      isArchivedStaffList: false,
      setIsArchivedStaffList: () => {},
      clearStaffSelection: () => {},
      toggleAssignment: () => {},
      selectAllAssignments: () => {},
      hasStaffSelection: false,
      pendingSelectedAssignmentIds: [],
      hasAnySelectedPending: false,
      setStaffTrackingRefetch: () => {},
      refetchStaffTracking: () => {},
    };
  }
  return ctx;
}
